/**
 * useMergedFormState — state management for MergedCreateEditForm.
 *
 * Owns all edit state (primary-field values, editable association targets, stage
 * ids, dirty flags, save/cancel) so MergedCreateEditForm can be thin wiring + JSX.
 * The three pieces that stay in the component are useActionEdit() (accordion
 * context), the useEffect that propagates isDirty into the accordion, and the
 * useLayoutEffect that writes save/cancel into the accordion's handler refs.
 */

import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type { EntityResult, EntityType } from '@/lib/search';

import { createProposalClient } from '../../lib/proposals/core/ProposalClient';
import type {
  AssociationAddedEntityPayload,
  ObjectCreatedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isAssociationAddedPayload } from '../../lib/proposals/core/types';
import { useProposalSelectionOptional } from '../../lib/proposals/hooks/ProposalSelectionContext';
import type { MergedAssociation } from '../../lib/proposals/utils/actionMerging';
import { getStableActionKey } from '../../lib/proposals/utils/helpers';
import {
  buildProposedEntityOptions,
  getSearchableTargetType,
  isStageAssociation,
  isSwappableAssociation,
  targetToEntityResult,
} from './associationTargets';
import { SaveValidationError } from './shared/SaveValidationError';
import { useCreateActionFields } from './useCreateActionFields';

interface UseMergedFormStateParams {
  primaryAction: ProposedAction<ObjectCreatedEntityPayload>;
  mergedAssociations: MergedAssociation[];
  orgId: number;
  proposalId?: number;
  onUpdate?: (updatedAction: ProposedAction) => void;
}

interface UseMergedFormStateResult {
  fieldKeys: string[];
  values: ReturnType<typeof useCreateActionFields>['values'];
  schema: ReturnType<typeof useCreateActionFields>['schema'];
  handleChange: ReturnType<typeof useCreateActionFields>['handleChange'];
  stageAssocs: MergedAssociation[];
  editableAssocs: MergedAssociation[];
  readOnlyAssocs: MergedAssociation[];
  editedTargets: Record<number, EntityResult | null>;
  handlersById: Record<number, (v: EntityResult | null) => void>;
  extraOptionsByType: Map<EntityType, EntityResult[]>;
  clearedIds: Set<number>;
  editedStageIds: Record<number, string | null>;
  handleStageIdChange: (actionId: number, stageId: string | null) => void;
  isDirty: boolean;
  submitAttempted: boolean;
  save: () => Promise<void>;
  cancel: () => void;
}

export function useMergedFormState({
  primaryAction,
  mergedAssociations,
  orgId,
  proposalId,
  onUpdate,
}: UseMergedFormStateParams): UseMergedFormStateResult {
  const {
    fieldKeys,
    values,
    schema,
    handleChange,
    isDirty: primaryDirty,
    buildPayload,
    reset: resetPrimary,
  } = useCreateActionFields(primaryAction, orgId);

  // Associations split into three buckets:
  //  - stage: pipeline/lifecycle stage → editable stage dropdown (not searchable,
  //    but pickable from the entity's pipeline).
  //  - editable: a searchable target type → editable entity picker (in-CRM or
  //    being created in this same proposal).
  //  - read-only: everything else.
  const stageAssocs = useMemo(
    () => mergedAssociations.filter(a => isStageAssociation(a.associationType)),
    [mergedAssociations]
  );
  const editableAssocs = useMemo(
    () =>
      mergedAssociations.filter(
        a => isSwappableAssociation(a) && !isStageAssociation(a.associationType)
      ),
    [mergedAssociations]
  );
  const readOnlyAssocs = useMemo(
    () =>
      mergedAssociations.filter(
        a =>
          !isSwappableAssociation(a) && !isStageAssociation(a.associationType)
      ),
    [mergedAssociations]
  );

  // Proposal-scoped picker options: entities being created elsewhere in this
  // proposal (currently checked) that match an association's target type. Reads
  // the sibling actions + live selection from the selection context; unchecking
  // a create action drops its entity here. Empty outside a proposal context.
  const selection = useProposalSelectionOptional();
  const proposalActions = selection?.actions;
  // `isActionSelected` is re-created whenever selection changes (it closes over
  // the selection map), so it alone drives the recompute below.
  const isActionSelected = selection?.selectionState.isSelected;
  const extraOptionsByType = useMemo(() => {
    const map = new Map<EntityType, EntityResult[]>();
    if (!proposalActions || !isActionSelected) {
      return map;
    }
    for (const assoc of editableAssocs) {
      const type = getSearchableTargetType(assoc);
      if (type && !map.has(type)) {
        map.set(
          type,
          buildProposedEntityOptions(proposalActions, isActionSelected, type)
        );
      }
    }
    return map;
  }, [editableAssocs, proposalActions, isActionSelected]);

  // Every per-row map in this hook is keyed by the association's VERSION-STABLE
  // identity (`getStableActionKey`), not its row id. A partial save can
  // re-version one association while the form stays open (a later association
  // or stage PATCH fails), and a raw-id key would orphan that row's edited
  // value and handler. The raw `action.id` is still what we PATCH against —
  // identity keys and API targets are different things.
  const initialTargets = useMemo(() => {
    const map: Record<number, EntityResult | null> = {};
    for (const assoc of editableAssocs) {
      map[getStableActionKey(assoc.action)] = targetToEntityResult(assoc);
    }
    return map;
  }, [editableAssocs]);

  // Seeded once on mount. The edit form unmounts when edit mode exits, so
  // re-entering edit remounts and re-seeds from the (possibly updated) props —
  // no in-place re-seed effect needed (which would risk wiping live edits).
  const [editedTargets, setEditedTargets] =
    useState<Record<number, EntityResult | null>>(initialTargets);

  const assocDirty = useMemo(
    () =>
      editableAssocs.some(
        assoc =>
          (editedTargets[getStableActionKey(assoc.action)]?.entity_id ??
            null) !== assoc.targetEntityId
      ),
    [editableAssocs, editedTargets]
  );

  // IDs of associations whose target was cleared (was non-null, now null).
  // Saving with a cleared target is blocked — we can't send a null target_entity_id.
  const clearedIds = useMemo(() => {
    const ids = new Set<number>();
    for (const assoc of editableAssocs) {
      const assocKey = getStableActionKey(assoc.action);
      if (
        editedTargets[assocKey] === null &&
        initialTargets[assocKey] !== null
      ) {
        ids.add(assocKey);
      }
    }
    return ids;
  }, [editableAssocs, editedTargets, initialTargets]);

  // Becomes true once a save is attempted with a cleared target, revealing
  // inline validation errors per row. Clears on cancel.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Stage associations: each MergedStageAssociationEditor reports its selected
  // stage entity_id here, seeded from the proposed target so an untouched stage
  // isn't dirty and isn't re-PATCHed. Seeded on mount only — the edit form
  // unmounts when edit mode exits, so re-entering remounts and re-seeds (same
  // as editedTargets above).
  const [editedStageIds, setEditedStageIds] = useState<
    Record<number, string | null>
  >(() =>
    Object.fromEntries(
      // Same identity key the stage editor reports under — see initialTargets.
      stageAssocs.map(a => [getStableActionKey(a.action), a.targetEntityId])
    )
  );
  const handleStageIdChange = useCallback(
    (actionId: number, stageId: string | null) => {
      setEditedStageIds(prev => ({ ...prev, [actionId]: stageId }));
    },
    []
  );
  const stageDirty = useMemo(
    () =>
      stageAssocs.some(
        a =>
          (editedStageIds[getStableActionKey(a.action)] ?? null) !==
          a.targetEntityId
      ),
    [stageAssocs, editedStageIds]
  );

  const isDirty = primaryDirty || assocDirty || stageDirty;

  const handleTargetChange = useCallback(
    (actionId: number, value: EntityResult | null) => {
      setEditedTargets(prev => ({ ...prev, [actionId]: value }));
    },
    []
  );

  // Stable per-row onChange handlers keyed by the association's version-stable
  // identity (see `initialTargets`). Built once per editableAssocs change so
  // each row's prop is a stable reference.
  const handlersById = useMemo((): Record<
    number,
    (v: EntityResult | null) => void
  > => {
    const result: Record<number, (v: EntityResult | null) => void> = {};
    for (const assoc of editableAssocs) {
      const assocKey = getStableActionKey(assoc.action);
      result[assocKey] = (v: EntityResult | null) =>
        handleTargetChange(assocKey, v);
    }
    return result;
  }, [editableAssocs, handleTargetChange]);

  const save = useCallback(async () => {
    if (!proposalId || !orgId) {
      throw new Error('proposalId and orgId are required to save');
    }

    // Block save if any association target was cleared — we cannot persist a
    // null target_entity_id. Reveal inline errors and keep the editor open.
    if (clearedIds.size > 0) {
      setSubmitAttempted(true);
      throw new SaveValidationError(
        'Please pick a target for each association or cancel.'
      );
    }

    const client = createProposalClient(orgId);

    if (primaryDirty) {
      try {
        const updated = await client.updateActionPayload(
          proposalId,
          primaryAction.id,
          { payload: buildPayload() }
        );
        onUpdate?.(updated);
      } catch (err) {
        console.error('Failed to save main record:', err);
        toast.error("We couldn't save the main record. Try again.");
        // Suppress the generic fallback toast and keep the editor open.
        throw new SaveValidationError();
      }
    }

    for (const assoc of editableAssocs) {
      // Identity key to read the edit; raw `action.id` below to PATCH it.
      const selected = editedTargets[getStableActionKey(assoc.action)];
      if (selected && selected.entity_id !== assoc.targetEntityId) {
        if (!isAssociationAddedPayload(assoc.action.payload)) {
          continue;
        }
        const current = assoc.action.payload as AssociationAddedEntityPayload;
        const updatedPayload: AssociationAddedEntityPayload = {
          ...current,
          payload: {
            ...current.payload,
            target_entity_id: selected.entity_id,
          },
        };
        try {
          const updated = await client.updateActionPayload(
            proposalId,
            assoc.action.id,
            { payload: updatedPayload }
          );
          onUpdate?.(updated);
        } catch (err) {
          console.error('Failed to save association:', err);
          const label = assoc.enrichedData?.object_label ?? 'association';
          toast.error(
            `We couldn't save the "${label}" association. Try again.`
          );
          // Suppress the generic fallback toast and keep the editor open.
          throw new SaveValidationError();
        }
      }
    }

    for (const assoc of stageAssocs) {
      // Identity key to read the edit; raw `action.id` below to PATCH it.
      const stageId = editedStageIds[getStableActionKey(assoc.action)];
      if (stageId && stageId !== assoc.targetEntityId) {
        if (!isAssociationAddedPayload(assoc.action.payload)) {
          continue;
        }
        const current = assoc.action.payload as AssociationAddedEntityPayload;
        const updatedPayload: AssociationAddedEntityPayload = {
          ...current,
          payload: {
            ...current.payload,
            target_entity_id: stageId,
          },
        };
        try {
          const updated = await client.updateActionPayload(
            proposalId,
            assoc.action.id,
            { payload: updatedPayload }
          );
          onUpdate?.(updated);
        } catch (err) {
          console.error('Failed to save stage:', err);
          const label = assoc.enrichedData?.object_label ?? 'stage';
          toast.error(`We couldn't save the "${label}" stage. Try again.`);
          // Suppress the generic fallback toast and keep the editor open.
          throw new SaveValidationError();
        }
      }
    }

    toast.success('Changes saved');
  }, [
    proposalId,
    orgId,
    clearedIds,
    primaryDirty,
    buildPayload,
    primaryAction.id,
    editableAssocs,
    editedTargets,
    stageAssocs,
    editedStageIds,
    onUpdate,
  ]);

  const cancel = useCallback(() => {
    resetPrimary();
    setEditedTargets(initialTargets);
    setEditedStageIds(
      Object.fromEntries(
        // Same identity key the stage editor reports under — see initialTargets.
        stageAssocs.map(a => [getStableActionKey(a.action), a.targetEntityId])
      )
    );
    setSubmitAttempted(false);
  }, [resetPrimary, initialTargets, stageAssocs]);

  return {
    fieldKeys,
    values,
    schema,
    handleChange,
    stageAssocs,
    editableAssocs,
    readOnlyAssocs,
    editedTargets,
    handlersById,
    extraOptionsByType,
    clearedIds,
    editedStageIds,
    handleStageIdChange,
    isDirty,
    submitAttempted,
    save,
    cancel,
  };
}
